<template>
  <div class="flex flex-col gap-4 w-full">
    <div v-for="(message, idx) in processedMessages" :key="message.id" class="message-container message-item" :data-message-id="message.id">
      <!-- 날짜 표시: 첫 메시지이거나, 이전 메시지와 날짜가 다를 때 (로딩 메시지에는 표시하지 않음) -->
      <div
        v-if="showDate && message.role !== 'loading' && message.timestamp && !isNaN(new Date(message.timestamp).getTime()) && (idx === 0 || !isSameDate(message.timestamp, processedMessages[idx - 1].timestamp))"
        class="w-full flex justify-center my-2"
      >
        <span class="text-xs text-gray-500 bg-white px-4 py-1 rounded-full shadow-sm border">
          {{ formatDate(message.timestamp) }}
        </span>
      </div>
      <!-- 로딩 메시지 (assistant와 동일한 레이아웃) -->
      <div v-if="message.role === 'loading'" class="flex justify-start mt-2">
        <div class="flex flex-col items-start max-w-[80%] group">
          <!-- 상단: 로고 + 모델명 -->
          <div class="flex items-center mb-1 ml-2">
            <img :src="require('@/assets/icons/logo_icon.png')" alt="logo" class="w-5 h-5 mr-2" />
            <span class="font-semibold text-gray-700">{{ message.modelName || '' }}</span>
          </div>
          <!-- 메시지 말풍선 -->
          <div class="text-black rounded-lg ml-5 p-4 relative w-full flex flex-col" style="min-width:180px;">
            <span class="break-all whitespace-pre-line flex items-center">
              <svg class="animate-spin h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              "{{ message.content.length > 30 ? message.content.slice(0, 30) + '...' : message.content }}" 위한 지식 기반 검색 중
            </span>
            <div class="flex items-center mt-2 w-full">              
              <button
                v-if="shouldShowTimestamp(message)"
                @click="copyMessage(message.content)"
                class="opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-gray-600 ml-4"
                title="메시지 복사"
              >
                <span class="material-icons" style="font-size: 14px;">content_copy</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- assistant 메시지 (로딩/실제 모두) -->
      <div v-else-if="message.role === 'assistant'" class="flex justify-start mt-2">
        <div class="flex flex-col items-start max-w-[80%] group">
          <!-- 상단: 로고 + 모델명 -->
          <div class="flex items-center mb-1 ml-2">
            <img :src="require('@/assets/icons/logo_icon.png')" alt="logo" class="w-5 h-5 mr-2" />
            <span class="font-semibold text-gray-700">{{ message.modelName || '' }}</span>
          </div>
          <!-- 메시지 말풍선 -->
          <div class="text-black rounded-lg ml-5 p-4 relative w-full flex flex-col chat-message-content" style="min-width:180px;">
            <span v-if="message.role === 'assistant' && message.isNotification === true" class="text-gray-400 flex items-center">
              <svg class="animate-spin h-4 w-4 mr-2 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              {{ message.content }}
            </span>
            <span v-else-if="message.role === 'assistant' && message.pending === true" class="text-gray-400">
              {{ message.content }}
            </span>
            <span v-else-if="message.role === 'assistant'" class="text-gray-900 break-all whitespace-pre-line" v-html="parsedMarkdown(message.content)"></span>
            <p v-if="debugMode && message.encryptedContent" class="break-all whitespace-pre-wrap mt-2 pt-2 border-t border-blue-400 text-xs text-black">{{ message.encryptedContent }}</p>
            
            <!-- reference_docs 표시 -->
            <div v-if="message.reference_docs && message.reference_docs.length > 0" class="mt-1" style="overflow-y: auto;">
              <div class="flex flex-row flex-wrap items-start min-w-0"> <div v-for="(doc, groupIndex) in getGroupedreference_docs(message.reference_docs)" :key="groupIndex" 
                    class="flex items-center p-1 mr-4"> <span class="mr-1 text-sm">{{ groupIndex + 1 }}.</span>
                  <div class="flex flex-col min-w-0">
                    <span
                      class="text-sm text-gray-400 hover:text-gray-600 truncate max-w-[180px] cursor-pointer"
                      @click="openReferenceModal(doc)"
                      :title="getDocFileName(doc)"
                    >
                      {{ getCleanRefName(getDocFileName(doc)) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex items-center mt-2 w-full">
              <span v-if="message.timestamp && !isNaN(new Date(message.timestamp).getTime()) && shouldShowTimestamp(message)" class="text-xs text-gray-500">{{ formatTime(message.timestamp) }}</span>
              <button
                v-if="shouldShowTimestamp(message)"
                @click="copyMessage(message.content)"
                class="opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-gray-600 ml-4"
                title="메시지 복사"
              >
                <span class="material-icons" style="font-size: 14px;">content_copy</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- 사용자 메시지 -->
      <div v-else-if="message.role === 'user'" class="flex justify-end">
        <div class="flex flex-col items-end w-[48rem] group">
          <!-- 첨부 파일 표시 (metadata.attachments, attachments, files) -->
          <div v-if="(message.metadata && message.metadata.attachments && message.metadata.attachments.length > 0) || (message.attachments && message.attachments.length > 0) || (message.files && message.files.length > 0)" class="mb-2 w-full" style="width: 170px;" >
            <!-- 1순위: metadata.attachments -->
            <template v-if="message.metadata && message.metadata.attachments && message.metadata.attachments.length > 0">
              <div v-for="(file, index) in message.metadata.attachments" :key="file.id || index" class="flex items-center gap-2 p-2 bg-gray-100 rounded cursor-pointer mb-1">
                <span class="material-icons">description</span>
                <span class="text-sm truncate" @click="openFilePreview(file)">{{ file.name }}</span>
              </div>
            </template>
            <!-- 2순위: attachments -->
            <template v-else-if="message.attachments && message.attachments.length > 0">
              <div v-for="(file, index) in message.attachments" :key="file.id || index" class="flex items-center gap-2 p-2 bg-gray-100 rounded cursor-pointer mb-1">
                <span class="material-icons">description</span>
                <span class="text-sm truncate" @click="openFilePreview(file)">{{ file.name }}</span>
              </div>
            </template>
            <!-- 3순위: files -->
            <template v-else-if="message.files && message.files.length > 0">
              <div v-for="(file, index) in message.files" :key="index" class="mb-2">
                <!-- 이미지 파일 -->
                <div v-if="isImageFile(file)" class="flex justify-end">
                  <div class="w-32 h-32 bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:opacity-90" @click.prevent="openImageModal(file)">
                    <img
                      :src="getFileUrl(file)"
                      :alt="file.name"
                      class="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <!-- PDF 파일 -->
                <div v-else-if="isPdfFile(file)" class="flex justify-end">
                  <div class="flex items-center gap-2 p-2 bg-gray-100 rounded cursor-pointer">
                    <button
                      class="text-sm text-blue-500 hover:text-blue-600"
                      @click.stop="downloadFile(file)"
                      title="다운로드"
                    >
                      다운로드
                    </button>
                  </div>
                </div>
                <!-- 기타 파일 -->
                <div v-else class="flex justify-end">
                  <div class="flex items-center gap-2 p-2 bg-gray-100 rounded cursor-pointer">
                    <span class="material-icons">description</span>
                    <span class="text-sm truncate" @click="openFilePreview(file)">{{ file.name }}</span>
                    <button
                      class="text-sm text-blue-500 hover:text-blue-600"
                      @click.stop="downloadFile(file)"
                      title="다운로드"
                    >
                      다운로드
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
          <!-- 메시지 말풍선 -->
          <div class="bg-gray-50 text-black rounded-lg p-3 relative">
            <p class="break-all whitespace-pre-wrap mb-0">{{ message.content }}</p>
            <p v-if="debugMode && message.encryptedContent" class="break-all whitespace-pre-wrap mt-2 pt-2 border-t border-blue-400 text-xs text-black">{{ message.encryptedContent }}</p>
          </div>
          <!-- 시간/복사 아이콘: 말풍선 바깥, 오른쪽 정렬 -->
          <div class="flex items-center justify-end mt-2 pr-2 w-full">
            <button
              v-if="shouldShowTimestamp(message)"
              @click="copyMessage(message.content)"
              class="opacity-0 group-hover:opacity-100 transition text-gray-400 hover:text-gray-600 mr-4"
              title="메시지 복사"
            >
              <span class="material-icons" style="font-size: 14px;">content_copy</span>
            </button>
            <span class="text-xs text-gray-500">{{ formatTime(message.timestamp) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Reference Modal -->
    <div v-if="showReferenceModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full h-full flex flex-col">
        <!-- Header -->
        <div class="flex-shrink-0 bg-white border-b border-gray-200 p-6 rounded-t-lg">
          <div class="flex justify-between items-center">
            <h3 class="text-xl font-bold text-gray-800">참고문헌</h3>
            <button @click="closeReferenceModal" class="text-gray-500 hover:text-gray-700 transition-colors" title="닫기">
              <span class="material-icons text-2xl">close</span>
            </button>
          </div>
        </div>
        
        <!-- Content Container -->
        <div class="flex-1 overflow-y-auto">
          <div class="p-6">
          <div v-if="Array.isArray(selectedReference)">
            <div v-for="(doc, index) in selectedReference" :key="index">
              <!-- 구분자 (첫 번째 항목이 아닐 때) -->
              <hr v-if="index > 0" class="my-6 border-gray-300">
              
              <!-- 인용 항목 -->
              <div class="space-y-4">
                <div class="text-lg font-semibold text-gray-800 border-l-4 border-blue-500 pl-3">
                  인용
                </div>
                
                <div class="space-y-3">
                  <!-- 출처 -->
                  <div>
                    <div class="text-sm font-medium text-gray-600 mb-1">출처</div>
                    <div class="text-gray-800">
                      <span class="text-gray-800">
                        {{ getCleanRefName(getDocFileName(doc)) }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- 관련도 -->
                  <div>
                    <div class="text-sm font-medium text-gray-600 mb-1">관련도</div>
                    <div class="text-gray-800">
                      <span :class="getDistanceColorClass(doc.metadata?.distance)" class="px-2 py-1 rounded">
                        {{ formatDistance(doc.metadata?.distance) }}%
                      </span>
                      <span class="ml-2 text-gray-500">({{ (doc.metadata?.distance || 0).toFixed(6) }})</span>
                    </div>
                  </div>
                  
                  <!-- 내용 -->
                  <div>
                    <div class="text-sm font-medium text-gray-600 mb-1">내용</div>
                    <div class="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <div class="text-gray-800 whitespace-pre-wrap leading-relaxed">{{ doc.content }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 단일 항목인 경우 -->
          <div v-else-if="selectedReference">
            <div class="space-y-4">
              <div class="text-lg font-semibold text-gray-800 border-l-4 border-blue-500 pl-3">
                인용
              </div>
              
              <div class="space-y-3">
                <!-- 출처 -->
                <div>
                  <div class="text-sm font-medium text-gray-600 mb-1">출처</div>
                  <div class="text-gray-800">
                    <span class="text-gray-800">
                      {{ getCleanRefName(getDocFileName(selectedReference)) }}
                    </span>
                  </div>
                </div>
                
                <!-- 관련도 -->
                <div>
                  <div class="text-sm font-medium text-gray-600 mb-1">관련도</div>
                  <div class="text-gray-800">
                    <span :class="getDistanceColorClass(selectedReference.metadata?.distance)" class="px-2 py-1 rounded">
                      {{ formatDistance(selectedReference.metadata?.distance) }}%
                    </span>
                    <span class="ml-2 text-gray-500">({{ (selectedReference.metadata?.distance || 0).toFixed(6) }})</span>
                  </div>
                </div>
                
                <!-- 내용 -->
                <div>
                  <div class="text-sm font-medium text-gray-600 mb-1">내용</div>
                  <div class="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div class="text-gray-800 whitespace-pre-wrap leading-relaxed">{{ selectedReference.content }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        
        <!-- Footer -->
        <div class="flex-shrink-0 bg-white border-t border-gray-200 p-6 rounded-b-lg">
          <div class="flex justify-center">
            <button 
              @click="closeReferenceModal"
              class="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              title="확인"
            >
              확인
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 이미지 모달 -->
    <ImageModal
      :show="showImageModal"
      :image-url="selectedImageUrl"
      :image-alt="selectedImageAlt"
      @close="closeImageModal"
    />

    <!-- FilePreviewModal -->
    <FilePreviewModal
      :show="showFilePreviewModal"
      :file="previewFile"
      @close="closeFilePreview"
    />

    <!-- 마스킹 모달 -->
    <AlertModal
      v-if="showMaskingModal"
      :visible="showMaskingModal"
      :message="''"
      size="wide"
    >
      <template #default>
        <div class="mb-4 mt-6">
          <div class="flex items-center mb-2">
            <img src="@/assets/icons/masking.png" alt="lock" class="w-5 h-5 mr-2" />
            <span class="text-gray-800 font-semibold text-lg">개인정보 보호</span>
  </div>
          <div class="text-gray-600 text-left pl-2">
            &bull;&nbsp;개인정보가 감지되어 자동으로 마스킹되었습니다. 원본 정보를 보내려면 체크를 해제하세요.
          </div>
        </div>
        <div class="w-full bg-gray-50 border border-gray-200 p-4 mt-4 mb-4 rounded-lg min-h-[48px] text-gray-800 text-sm font-medium text-left" style="white-space:pre-wrap;word-break:break-all;line-height:1.6;">
          <span
            v-for="(part, i) in getMaskedParts()"
            :key="'part-'+i"
            class="mr-1 align-middle"
          >
            <template v-if="part.type === 'pii'">
              <span class="inline-flex items-center space-x-1">
                <input
                  type="checkbox"
                  v-model="maskingState.masked[part.idx]"
                  @change="updateMasked"
                  class="no-focus-checkbox"
                  tabindex="-1"
                />
                <span class="inline-block break-all bg-amber-100 text-amber-800 px-2 py-1 rounded-md border border-amber-200 font-bold">
                  {{ getMaskedText(part, maskingState.masked[part.idx]) }}
                </span>
              </span>
            </template>
            <template v-else>
              <span>{{ part.text }}</span>
            </template>
          </span>
        </div>
        <div class="flex justify-center gap-3 mt-6 mb-2">
          <button 
            class="px-16 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-300 min-w-[100px] transition-colors" 
            @click="handleMaskingCancel"
            title="취소"
          >
            취소
          </button>
          <button 
            class="px-16 py-2 bg-blue-200 text-white rounded-lg hover:bg-blue-500 min-w-[100px] transition-colors" 
            @click="resendWithMaskingOverride"
            title="전송"
          >
            전송
          </button>
        </div>
      </template>
    </AlertModal>

    <!-- 필터링 모달 -->
    <FilteringModal
      :visible="showFilteringModal"
      :error-message="filteringErrorMessage"
      :is-file-pii="filteringErrorCode === '2106'"
      :single-button="filteringSingleButton"
      @cancel="handleFilteringCancel"
      @confirm="resendWithFilteringOverride"
    />

    <!-- 일반 알림 모달 -->
    <AlertModal :visible="showAlertModal" :message="alertMessage" size="default" @confirm="showAlertModal = false" />
  </div>
</template>

<script>
import ImageModal from './ImageModal.vue'
import FilePreviewModal from './FilePreviewModal.vue'
import AlertModal from '@/components/common/AlertModal.vue'
import FilteringModal from './FilteringModal.vue'
import { downloadFileBlob, downloadAndSaveFile, secureChatAPI } from '@/utils/api'
import { handleApiError } from '@/utils/apiErrorHandler';
import { getErrorMessage } from '@/utils/errorCodes'
import showdown from 'showdown'
const converter = new showdown.Converter()

export default {
  name: 'ChatMessage',
  components: {
    ImageModal,
    FilePreviewModal,
    AlertModal,
    FilteringModal
  },
  props: {
    messages: {
      type: Array,
      required: true
    },
    debugMode: {
      type: Boolean,
      default: false
    },
    showDate: {
      type: Boolean,
      default: true
    },
    isWaitingResponse: {
      type: Boolean,
      default: false
    },
    selectedModel: {
      type: Object,
      default: null
    },
    currentChat: {
      type: Object,
      default: null
    }
  },
  emits: ['message-success', 'pause', 'remove-loading-message', 'add-loading-message'],
  data() {
    return {
      editingMessage: null,
      editContent: '',
      showImageModal: false,
      selectedImageUrl: '',
      selectedImageAlt: '',
      loadingDotCount: 1,
      loadingInterval: null,
      showFilePreviewModal: false,
      previewFile: null,
      showReferenceModal: false,
      selectedReference: null,
      // 채팅 로직 관련 추가 데이터
      showMaskingModal: false,
      showFilteringModal: false,
      showAlertModal: false,
      alertMessage: '',
      maskingState: null,
      filteringErrorMessage: '',
      filteringErrorCode: '',
      filteringSingleButton: false,
      originalPayload: null,
      overrideOptions: {},
      maskingParts: [] // 마스킹 파트 배열 추가
    }
  },
  computed: {
    processedMessages() {
      const processed = this.messages
        .filter(message => message && message.id) // id가 없는 메시지 필터링
        .map((message) => {
        return message;
      });
      return processed;
    }
  },
  mounted() {
    if (this.isWaitingResponse) {
      this.startLoadingAnimation();
    }
  },
  watch: {
    isWaitingResponse(newVal) {
      if (newVal) {
        this.startLoadingAnimation();
      } else {
        this.stopLoadingAnimation();
      }
    }
  },
  methods: {
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString()
    },
    formatDate(timestamp) {
      const date = new Date(timestamp)
      const days = ['일', '월', '화', '수', '목', '금', '토']
      return `${date.getFullYear()}. ${String(date.getMonth() + 1).padStart(2, '0')}. ${String(date.getDate()).padStart(2, '0')}. (${days[date.getDay()]})`
    },
    isSameDate(ts1, ts2) {
      if (!ts1 || !ts2) return false
      const d1 = new Date(ts1)
      const d2 = new Date(ts2)
      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      )
    },
    async copyMessage(content) {
      try {
        // 최신 Clipboard API 사용 (HTTPS 환경)
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(content);
          console.log('클립보드 복사 성공');
        } else {
          // 대체 방법: document.execCommand 사용
          this.fallbackCopyToClipboard(content);
        }
      } catch (err) {
        console.warn('Clipboard API 실패, 대체 방법 사용:', err);
        // 에러 발생 시 대체 방법 시도
        this.fallbackCopyToClipboard(content);
      }
    },
    fallbackCopyToClipboard(text) {
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          console.log('대체 방법으로 클립보드 복사 성공');
        } else {
          console.error('클립보드 복사 실패');
        }
      } catch (err) {
        console.error('클립보드 복사 실패:', err);
      }
    },
    startEdit(message) {
      this.editingMessage = message
      this.editContent = message.content
    },
    saveEdit() {
      if (this.editingMessage) {
        this.$emit('edit-message', {
          messageId: this.editingMessage.id,
          content: this.editContent
        })
      }
      this.editingMessage = null
      this.editContent = ''
    },
    handleSendClick(message) {
      if (this.editingMessage?.id === message.id) {
        this.saveEdit()
      }
    },
    isImageFile(file) {
      if (!file || !file.type) return false
      const imageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
      return imageTypes.includes(file.type)
    },
    isPdfFile(file) {
      if (!file || !file.name) return false
      return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
    },
    async getFileUrl(file) {
      if (file instanceof File || file instanceof Blob) {
        return URL.createObjectURL(file)
      }
      if (file && file.url) {
        return file.url
      }
      if (file && file.id) {
        // 서버에서 Blob 받아오기
        try {
          const blob = await downloadFileBlob(file.id)
          return URL.createObjectURL(blob)
        } catch (e) {
          return ''
        }
      }
      return ''
    },
    getFileIcon(file) {
      // 파일 타입에 따른 아이콘 반환
      const fileType = file.type || ''
      if (fileType.includes('pdf')) {
        return require('@/assets/icons/pdf.png')
      }
      if (fileType.includes('word') || fileType.includes('document')) {
        return require('@/assets/icons/doc.png')
      }
      return require('@/assets/icons/file.png')
    },
    getFileType(file) {
      // 파일 확장자 추출
      const extension = file.name.split('.').pop().toUpperCase()
      return `${extension} 파일`
    },
    async openImageModal(file) {
      if (!file) return;
      const url = await this.getFileUrl(file)
      this.selectedImageUrl = url
      this.selectedImageAlt = file.name
      this.showImageModal = true
    },
    closeImageModal() {
      this.showImageModal = false
      this.selectedImageUrl = ''
      this.selectedImageAlt = ''
    },
    startLoadingAnimation() {
      this.loadingDotCount = 1;
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      this.loadingInterval = setInterval(() => {
        this.loadingDotCount = this.loadingDotCount % 5 + 1;
      }, 400);
    },
    stopLoadingAnimation() {
      if (this.loadingInterval) {
        clearInterval(this.loadingInterval);
        this.loadingInterval = null;
      }
      this.loadingDotCount = 1;
    },
    async openFilePreview(file) {
      console.log('openFilePreview 호출됨', file);
      console.log('file.id:', file.id, 'file.blobUrl:', file.blobUrl);
      if (!file) return;
      if (!file.blobUrl && file.id) {
        console.log('fetch 실행!');
        try {
          const blob = await downloadFileBlob(file.id);
          file.blobUrl = URL.createObjectURL(blob);
          console.log('blobUrl 생성됨:', file.blobUrl);
        } catch (e) {
          console.log('fetch 실패:', e.message || e);
          return;
        }
      }
      this.previewFile = { ...file };
      this.showFilePreviewModal = true;
    },
    closeFilePreview() {
      this.showFilePreviewModal = false
      this.previewFile = null
    },
    async downloadFile(file) {
      try {
        await downloadAndSaveFile(file.id, file.name);
      } catch (e) {
        alert('파일 다운로드 중 오류가 발생했습니다: ' + (e.message || e));
      }
    },
    parsedMarkdown(content) {
      if (!content) return ''
      // content가 JSON string이면 파싱해서 content 필드만 반환
      if (typeof content === 'string' && content.trim().startsWith('{') && content.trim().endsWith('}')) {
        try {
          const obj = JSON.parse(content);
          if (obj && typeof obj === 'object' && obj.content) {
            return converter.makeHtml(obj.content);
          }
        } catch (e) { /* 무시 */ }
      }
      return converter.makeHtml(content)
    },
    getGroupedreference_docs(docs) {
      // console.log('[ChatMessage.vue] getGroupedreference_docs 시작 - 입력 데이터:', docs);
      
      if (!docs || !Array.isArray(docs)) {
        // console.log('[ChatMessage.vue] docs가 배열이 아니거나 없음:', docs);
        return [];
      }

      // 파일명으로 그룹화
      const groupedByFileName = docs.reduce((acc, doc) => {
        const fileName = doc.metadata?.file_name || '알 수 없는 파일';
        
        if (!acc[fileName]) {
          acc[fileName] = [];
        }
        acc[fileName].push(doc);
        return acc;
      }, {});


      // 그룹화된 결과를 배열로 변환
      const result = Object.values(groupedByFileName).map(group => {
        if (group.length === 1) {
          return group[0];
        }
        return group;
      });

      return result;
    },
    getDocFileName(doc) {
      
      if (Array.isArray(doc)) {
        const fileName = doc[0]?.metadata?.file_name || '알 수 없는 파일';
        return fileName;
      }
      
      const fileName = doc?.metadata?.file_name || '알 수 없는 파일';
      return fileName;
    },
    getDocDistance(doc) {
      
      if (Array.isArray(doc)) {
        const distance = doc[0]?.metadata?.distance;
        return distance;
      }
      
      const distance = doc?.metadata?.distance;
      return distance;
    },
    formatDistance(distance) {
      if (distance === undefined || distance === null) {
        return '0.0';
      }
      return ((distance) * 100).toFixed(1);
    },
    getDistanceColorClass(distance) {
      const percentage = parseFloat(this.formatDistance(distance));
      
      if (percentage >= 80) {
        return 'bg-green-200 text-green-900';
      } else if (percentage >= 60) {
        return 'bg-yellow-200 text-yellow-900';
      } else if (percentage >= 40) {
        return 'bg-yellow-300 text-yellow-900';
      } else {
        return 'bg-red-200 text-red-900';
      }
    },
    openReferenceModal(reference) {
      this.selectedReference = reference;
      this.showReferenceModal = true;
    },
    closeReferenceModal() {
      this.showReferenceModal = false;
      this.selectedReference = null;
    },
    async downloadReferenceFile(doc) {
      try {
        const raw = doc?.metadata?.source_path || '';
        let fileId = raw;
        if (typeof raw === 'string') {
          const match = raw.match(/(\d+)(?=__|$)/);
          if (match) fileId = match[1];
        }
        await downloadAndSaveFile(fileId, doc.metadata.file_name);
      } catch (e) {
        alert('파일 다운로드 중 오류가 발생했습니다: ' + (e.message || e));
      }
    },
    shouldShowTimestamp(message) {
      // 1) 현재 페이지가 응답을 기다리는 중이라면(스트리밍 중) 마지막 assistant 메시지의 시간은 숨김
      if (this.isWaitingResponse) {
        const lastMsg = this.processedMessages[this.processedMessages.length - 1] || null;
        if (lastMsg && lastMsg.id === message.id) {
          return false; // 아직 스트리밍 중인 마지막 메시지
        }
      }

      // 2) 명시적으로 pending=true 인 경우(타이핑 중)도 숨김
      if (message.pending === true) {
        return false;
      }

      return true; // 위 조건을 모두 통과하면 시간 표시
    },
    getCleanRefName(fileName) {
      if (!fileName) return '';
      // "숫자__" 또는 "숫자. " 등 가변 접두어 패턴 제거
      return fileName.replace(/^\d+__\s*/, '');
    },
    // ChatInput에서 온 메시지 처리
    async handleSendMessage(userMessage) {
      console.log('[ChatMessage.vue] 메시지 수신:', userMessage);
      
      if (!userMessage.content.trim() && (!userMessage.files || userMessage.files.length === 0)) {
        return;
      }
      
      try {
        // 1. 사용자 메시지를 즉시 표시
        const newUserMessage = {
          id: 'user_' + Date.now(),
          role: 'user',
          content: userMessage.content,
          files: userMessage.files || [],
          metadata: userMessage.files && userMessage.files.length > 0 ? {
            attachments: userMessage.files.map(f => ({
              id: f.id,
              name: f.name,
              size: f.size,
              mime_type: f.mime_type || 'application/pdf'
            }))
          } : undefined,
          timestamp: new Date().toISOString()
        };
        
        // 2. 로딩 메시지 추가
        const loadingMessage = {
          id: 'loading_' + Date.now(),
          role: 'loading',
          content: userMessage.content,
          modelName: this.selectedModel?.name || 'AI',
          timestamp: new Date().toISOString()
        };
        
        // 부모에게 사용자 메시지와 로딩 메시지 모두 추가 요청
        this.$emit('message-success', {
          userMessage: newUserMessage,
          loadingMessage: loadingMessage
        });
        
        // 3. API 호출 (로딩 상태는 설정하지 않음)
        await this.processApiRequest(userMessage);
        
      } catch (err) {
        console.error('[ChatMessage.vue] 메시지 처리 중 오류:', err);
        this.alertMessage = '네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
        this.showAlertModal = true;
      }
    },
    async processApiRequest(userMessage, response = null, overrides = null) {
      if (!response) {
        let payload;
        
        if (userMessage) {
          // 새로운 메시지 전송
          payload = {
            model: this.selectedModel?.id || 'gemma3:4b',
            messages: [
              {
                role: 'user',
                content: userMessage.content.trim(),
                metadata: userMessage.files && userMessage.files.length > 0 ? {
                  attachments: userMessage.files.map(f => ({
                    id: f.id,
                    name: f.name,
                    size: f.size,
                    mime_type: f.mime_type || 'application/pdf'
                  }))
                } : undefined
              }
            ],
            stream: false
          };
          
          // 원본 payload 저장 및 우회 옵션 초기화/유지
          this.originalPayload = payload;
          this.overrideOptions = overrides || {};
        } else {
          // 재전송 (우회 등) - 기존 payload 사용
          payload = this.originalPayload;
          this.overrideOptions = { ...this.overrideOptions, ...(overrides || {}) };
        }
        
        try {
          const api = secureChatAPI;
          const chatId = this.currentChat?.id || 'default';
          response = await api.sendMessage(chatId, payload, this.overrideOptions);
        } catch (err) {
          response = err;
        }
      } else if (userMessage && response) {
        // 새채팅에서 에러 처리를 위해 호출된 경우 - originalPayload 설정
        if (!this.originalPayload) {
          this.originalPayload = {
            model: this.selectedModel?.id || 'gemma3:4b',
            messages: [
              {
                role: 'user',
                content: userMessage.content.trim(),
                metadata: userMessage.files && userMessage.files.length > 0 ? {
                  attachments: userMessage.files.map(f => ({
                    id: f.id,
                    name: f.name,
                    size: f.size,
                    mime_type: f.mime_type || 'application/pdf'
                  }))
                } : undefined
              }
            ],
            stream: false
          };
          this.overrideOptions = overrides || {};
        }
      }
      
      // 200이 아닌 모든 상태 코드를 여기서 처리합니다.
      if (response && response.status && response.status !== 200) {
        // 에러 코드 추출: error.code, _meta.code, 또는 _meta.detail.code
        let metaDetail = response._meta?.detail;
        if (typeof metaDetail === 'string') {
          try { metaDetail = JSON.parse(metaDetail); } catch (e) { /* 그대로 사용 */ }
        }
        const errorCode = response.error?.code?.toString() || response._meta?.code?.toString() || metaDetail?.code?.toString();

        console.log('[processApiRequest] response:', { status: response?.status, error: response?.error, _meta: response?._meta, metaDetail, errorCode });

        // 에러 코드 기반 분기 (HTTP 상태코드와 무관하게 처리)
        if (errorCode) {
            // 파일 PII (2106)
            if (errorCode === '2106') {
              this.addErrorMessageToChat(response);
              this.filteringErrorCode = errorCode;
              this.filteringErrorMessage = '파일 내용에 개인식별정보(PII)가 감지되었습니다.';
              this.showFilteringModal = true;
              return;
            }

            // 탈옥/보안 우회 (2201-2299)
            if (errorCode >= '2201' && errorCode <= '2299') {
              this.addErrorMessageToChat(response);
              this.filteringErrorCode = errorCode;
              this.filteringErrorMessage = 'AI 안전성 정책 위반으로 요청이 거부되었습니다. AI 거버넌스 표준 준수를 위한 조치입니다.';
              this.filteringSingleButton = true;
              this.showFilteringModal = true;
              return;
            }

            // 금지된 단어 요청 (2301)
            if (errorCode === '2301') {
              this.addErrorMessageToChat(response);
              this.filteringErrorCode = errorCode;
              this.filteringErrorMessage = '금지된 단어가 포함되어 있습니다.';
              this.filteringSingleButton = true;
              this.showFilteringModal = true;
              return;
            }

            // 일반 PII (2101-2199)
            if (errorCode >= '2101' && errorCode <= '2199') {
              const hasToxicFilter = !!(response.error?.additional?.toxicFilter || metaDetail?.predictions?.toxicFilter);
              await this.handlePiiValidation(response, hasToxicFilter);
              return;
            }

            // 컨텐츠 필터링 (2001-2006)
            if (errorCode >= '2001' && errorCode <= '2006') {
              this.addErrorMessageToChat(response);
              await this.handleFilteringValidation(response);
              return;
            }
        }

        // 에러 코드가 없거나 매칭 안 되는 경우 ApiErrorHandler에게 맡김
        handleApiError(response, {
            showModal: (msg) => {
                this.alertMessage = msg;
                this.showAlertModal = true;
                this.$emit('remove-loading-message');
            },
            handleMasking: (res) => this.handlePiiValidation(res)
        });
        return;
      }

      // 정상 응답 처리
      this.$emit('message-success', {
        userMessage: null, // 이미 추가됨
        serverResponse: response
      });
      
      // 로딩 메시지 제거 요청
      this.$emit('remove-loading-message');
    },
    // 새로운 로딩 메시지 추가 요청 메서드
    requestAddLoadingMessage() {
      // 이미 로딩 메시지가 있으면 요청하지 않음
      if (this.messages.some(m => m.role === 'loading')) {
        return;
      }
      
      const loadingMsg = {
        id: 'loading_' + Date.now(),
        role: 'loading',
        content: this.originalPayload?.messages?.[0]?.content || '메시지 처리 중...',
        modelName: this.selectedModel?.name || this.selectedModel?.id || '',
        timestamp: null
      };
      
      this.$emit('add-loading-message', loadingMsg);
    },
    async handlePiiValidation(response, hasToxicFilter = false) {
      try {
        // SDK 구조: response._meta.detail에 predictions 포함
        // 레거시 구조: response.error.additional에 detectedPii 포함
        let detail = response._meta?.detail || response.error?.additional;

        if (typeof detail === 'string') {
          try { detail = JSON.parse(detail); } catch (e) { /* 그대로 사용 */ }
        }

        // detail.predictions.detectedPii (SDK) 또는 detail.detectedPii (레거시)
        const predictions = detail?.predictions?.detectedPii || (Array.isArray(detail?.predictions) ? detail.predictions : null) || detail?.detectedPii || [];

        if (!predictions || predictions.length === 0) {
          this.alertMessage = response.error?.message || response._meta?.detail || '개인정보가 감지되었습니다.';
          this.showAlertModal = true;
          return;
        }

        // 원본 사용자 메시지
        const userContent = this.originalPayload?.messages?.[0]?.content || detail?.userContent || '';

        // maskedContent 기준 오프셋 계산
        const sortedByStart = [...predictions].sort((a, b) => a.originalStart - b.originalStart);
        let maskedContent = '';
        let lastOrigIdx = 0;
        const adjustedPredictions = sortedByStart.map((pred) => {
          maskedContent += userContent.slice(lastOrigIdx, pred.originalStart);
          const newStart = maskedContent.length;
          maskedContent += pred.maskedText;
          const newEnd = maskedContent.length;
          lastOrigIdx = pred.originalEnd;
          return { ...pred, start: newStart, end: newEnd };
        });
        maskedContent += userContent.slice(lastOrigIdx);

        this.maskingState = {
          maskedUserContent: maskedContent,
          userContent: userContent,
          detectedPii: adjustedPredictions,
          masked: adjustedPredictions.map(() => true),
          hasToxicFilter
        };

        this.initializeMaskingParts();

        this.showAlertModal = false;
        this.showMaskingModal = true;
      } catch (err) {
        console.error('[ChatMessage.vue] PII 검증 처리 중 오류:', err);
        this.alertMessage = '검증 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    async handleFilteringValidation(response) {
      try {
        this.filteringErrorCode = response.error?.code;
        this.filteringErrorMessage = getErrorMessage(this.filteringErrorCode);
        
        this.showAlertModal = false;
        this.showFilteringModal = true;
      } catch (err) {
        console.error('[ChatMessage.vue] 필터링 검증 처리 중 오류:', err);
        this.alertMessage = '검증 처리 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    getMaskedParts() {
      if (!this.maskingParts || this.maskingParts.length === 0) return [];
      
      return this.maskingParts.map(part => {
        if (part.isPii) {
          return {
            type: 'pii',
            idx: part.index,
            text: part.originalText,
            entityType: part.entityType,
            maskedText: part.maskedText
          };
        } else {
          return {
            type: 'text',
            text: part.text
          };
        }
      });
    },
    getMaskedText(item, masked) {
      // 서버가 제공한 maskedText 사용
      if (masked) {
        return item.maskedText || item.text || '';
      }
      return item.originalText || item.text || '';
    },
    getMaskedTypeKor(item) {
      if (item.entityType === 'KR_PHONE') {
        return '전화번호';
      }
      if (item.entityType === 'KR_ID') {
        return '주민번호';
      }
      if (item.entityType === 'KR_PERSON') {
        return '이름';
      }
      if (item.entityType === 'EMAIL_ADDRESS') {
        return '이메일';
      }
      if (item.entityType === 'KR_CREDIT_CARD') {
        return '카드번호';
      }
      if (item.entityType === 'KR_ADDRESS_UNIT') {
        return '주소';
      }
      if (item.entityType === 'KR_PASSPORT') {
        return '여권번호';
      }
      return '개인정보';
    },
    getMaskedUserContent() {
      if (!this.maskingParts || this.maskingParts.length === 0) return '';
      return this.maskingParts.map(part => part.text).join('');
    },
    initializeMaskingParts() {
      if (!this.maskingState) return;
      const { maskedUserContent, detectedPii } = this.maskingState;
      const parts = [];
      let lastIdx = 0;
      
      detectedPii.forEach((item, i) => {
        const start = item.start;
        const end = item.end;
        
        // 일반 텍스트 부분 추가
        if (start > lastIdx) {
          parts.push({
            text: maskedUserContent.slice(lastIdx, start),
            isPii: false,
            index: -1
          });
        }
        
        // PII 부분 추가
        parts.push({
          text: maskedUserContent.slice(start, end), // 현재는 마스킹된 텍스트
          isPii: true,
          index: i,
          originalText: item.originalText,
          maskedText: item.maskedText,
          entityType: item.entityType
        });
        
        lastIdx = end;
      });
      
      // 마지막 일반 텍스트 부분 추가
      if (lastIdx < maskedUserContent.length) {
        parts.push({
          text: maskedUserContent.slice(lastIdx),
          isPii: false,
          index: -1
        });
      }
      
      this.maskingParts = parts;
    },
    updateMasked() {
      if (!this.maskingParts || !this.maskingState) return;
      
      // 각 PII 파트의 텍스트를 체크박스 상태에 따라 업데이트
      this.maskingParts.forEach(part => {
        if (part.isPii) {
          const isChecked = this.maskingState.masked[part.index];
          if (isChecked) {
            part.text = this.getMaskedText({
              text: part.originalText,
              entityType: part.entityType,
              maskedText: part.maskedText
            }, true);
          } else {
            part.text = part.originalText;
          }
        }
      });
      
      // 최종 결과 업데이트
      this.maskingState.maskedUserContent = this.maskingParts.map(part => part.text).join('');
    },
    handleMaskingCancel() {
      this.showMaskingModal = false;
      this.maskingState = null;
      this.maskingParts = [];
      this.overrideOptions = {};
      this.originalPayload = null;
      
      // 부모에게 응답 완료 알림
      this.$emit('message-success', {
        userMessage: null,
        serverResponse: null,
        cancelled: true
      });
    },
    async resendWithMaskingOverride() {
      try {
        const maskedContent = this.getMaskedUserContent();
        if (!maskedContent || maskedContent.trim().length === 0) {
          this.alertMessage = '마스킹된 메시지가 비어 있습니다. 다시 입력해 주세요.';
          this.showAlertModal = true;
          return;
        }
        
        // 모달 닫기 전에 필요한 값 미리 저장
        const hasToxicFilter = this.maskingState?.hasToxicFilter || false;
        
        // ✅ 즉시 마스킹 모달 닫기
        this.showMaskingModal = false;
        this.maskingState = null;
        this.maskingParts = [];

        // ✅ DOM 업데이트 대기 - 모달 닫힘 완료 후 메시지 처리
        await this.$nextTick();

        // ✅ 기존 사용자 메시지의 content를 마스킹된 내용으로 교체
        const originalContent = this.originalPayload?.messages?.[0]?.content || '';
        const lastUserIdx = [...this.messages].reverse().findIndex(m => m.role === 'user' && m.content === originalContent);
        if (lastUserIdx !== -1) {
          const actualIdx = this.messages.length - 1 - lastUserIdx;
          this.$emit('update-user-message', { index: actualIdx, content: maskedContent });
        }

        // 로딩 메시지 추가
        const loadingMessage = {
          id: 'loading_' + Date.now(),
          role: 'loading',
          content: maskedContent,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          timestamp: null
        };
        this.$emit('add-loading-message', loadingMessage);

        // ✅ DOM 업데이트 대기 - 메시지 추가 완료 후 API 호출
        await this.$nextTick();
        
        // 수정된 payload로 재전송 (우회 옵션을 payload에 포함)
        const payload = {
          ...this.originalPayload,
          overridePrivacyFilter: true,
          messages: [{
            ...this.originalPayload.messages[0],
            content: maskedContent,
            metadata: {
              attachments: this.originalPayload.messages[0].metadata?.attachments || []
            }
          }]
        };

        // 필터링도 함께 있었다면 필터링 우회도 추가
        if (hasToxicFilter) {
          payload.overrideToxicFilter = true;
        }

        // 원본 payload 업데이트 (마스킹된 내용으로)
        this.originalPayload = payload;

        // 우회 옵션도 동기화
        this.overrideOptions = {
          ...this.overrideOptions,
          overridePrivacyFilter: true,
          ...(hasToxicFilter ? { overrideToxicFilter: true } : {})
        };

        const api = secureChatAPI;
        const chatId = this.currentChat?.id || 'default';
        let response;
        try {
          response = await api.sendMessage(chatId, payload, this.overrideOptions);
        } catch (err) {
          response = err;
        }

        // API 호출 완료 후 로딩 메시지 즉시 제거
        this.$emit('remove-loading-message');

        // 에러 응답인 경우 processApiRequest로 위임 (422, 400 등 모두 처리)
        if (response && response.status && response.status !== 200) {
          this.overrideOptions = { ...this.overrideOptions };
          return this.processApiRequest(null, response, this.overrideOptions);
        }

        // 성공 시 처리
        this.$emit('message-success', {
          userMessage: null, // 이미 추가됨
          serverResponse: response
        });

      } catch (err) {
        console.error('[ChatMessage.vue] 마스킹 재전송 중 오류:', err);
        this.$emit('remove-loading-message');
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    handleFilteringCancel() {
      this.showFilteringModal = false;
      this.filteringErrorMessage = '';
      this.filteringErrorCode = '';
      this.filteringSingleButton = false;
      this.overrideOptions = {};
      this.originalPayload = null;
      
      // 부모에게 응답 완료 알림
      this.$emit('message-success', {
        userMessage: null,
        serverResponse: null,
        cancelled: true
      });
    },
    async resendWithFilteringOverride() {
      try {
        // 모달 닫기 전에 필요한 값 미리 저장
        const errorCode = this.filteringErrorCode;
        
        // ✅ 즉시 필터링 모달 닫기
        this.showFilteringModal = false;
        this.filteringErrorMessage = '';
        this.filteringErrorCode = '';
        this.filteringSingleButton = false;
        
        // ✅ DOM 업데이트 대기 - 모달 닫힘 완료 후 메시지 처리
        await this.$nextTick();
        
        // 재전송할 사용자 메시지 먼저 추가
        const originalContent = this.originalPayload?.messages?.[0]?.content || '';
        const newUserMessage = {
          id: 'user_' + Date.now(),
          role: 'user',
          content: originalContent,
          timestamp: Date.now() - 2000,
          modelName: this.selectedModel?.name || this.selectedModel?.id || ''
        };
        
        // 파일 첨부가 있다면 추가
        if (this.originalPayload?.messages?.[0]?.metadata?.attachments?.length > 0) {
          newUserMessage.metadata = {
            attachments: this.originalPayload.messages[0].metadata.attachments
          };
        }
        
        // 로딩 메시지 생성 (timestamp 조정으로 순서 보장)
        const loadingMessage = {
          id: 'loading_' + Date.now(),
          role: 'loading',
          content: originalContent,
          modelName: this.selectedModel?.name || this.selectedModel?.id || '',
          timestamp: Date.now() - 1000 // 사용자 메시지보다 늦은 시간
        };
        
        // 사용자 메시지와 로딩 메시지를 함께 추가
        this.$emit('message-success', {
          userMessage: newUserMessage,
          serverResponse: null,
          loadingMessage: loadingMessage
        });
        
        // ✅ DOM 업데이트 대기 - 메시지 추가 완료 후 API 호출
        await this.$nextTick();
        
        // 파일 PII(2106)인 경우 overridePrivacyFilter, 그 외 overrideToxicFilter
        const currentOverride = errorCode === '2106'
          ? { overridePrivacyFilter: true }
          : { overrideToxicFilter: true };

        // 우회 옵션 누적 (기존 옵션 유지하면서 추가)
        const updatedOverrides = {
          ...this.overrideOptions,
          ...currentOverride
        };

        // ✅ payload body에 우회 옵션 포함 (서버가 읽는 곳)
        const payload = {
          ...this.originalPayload,
          ...currentOverride
        };
        this.originalPayload = payload;
        this.overrideOptions = updatedOverrides;

        const api = secureChatAPI;
        const chatId = this.currentChat?.id || 'default';
        let response;
        try {
          response = await api.sendMessage(chatId, payload, updatedOverrides);
        } catch (err) {
          response = err;
        }

        // API 호출 완료 후 로딩 메시지 즉시 제거
        this.$emit('remove-loading-message');

        // 에러 응답인 경우 processApiRequest로 위임
        if (response && response.status && response.status !== 200) {
          return this.processApiRequest(null, response, updatedOverrides);
        }

        // 성공 시 처리
        this.$emit('message-success', {
          userMessage: null, // 이미 추가됨
          serverResponse: response
        });

      } catch (err) {
        console.error('[ChatMessage.vue] 필터링 재전송 중 오류:', err);
        this.$emit('remove-loading-message');
        this.alertMessage = '메시지 전송 중 오류가 발생했습니다.';
        this.showAlertModal = true;
      }
    },
    // 422 에러 응답 메시지를 채팅 화면에 실시간 표시
    addErrorMessageToChat(response) {
      // 로딩 메시지 제거 요청
      this.$emit('remove-loading-message');
      
      // 서버 응답 메시지 생성 (error가 문자열/객체 모두 처리)
      const errorMessage = response.message
        || (typeof response.error === 'string' ? response.error : response.error?.message)
        || '서버 오류가 발생했습니다.';
      
      // processServerResponse 직접 호출하여 중복 방지
      this.$parent.processServerResponse({
        role: 'assistant',
        content: errorMessage,
        timestamp: Date.now(),
        isError: true
      });
    }
  },
}
</script>

<style scoped>
.message-container {
  margin-bottom: 1rem;
}

.material-icons {
  font-family: 'Material Icons';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}

.chat-message-content ul,
.chat-message-content ol {
  list-style-type: disc !important;
  margin-left: 1.5em !important;
  padding-left: 1.2em !important;
}
.chat-message-content ol {
  list-style-type: decimal !important;
}
.chat-message-content li {
  margin-bottom: 0.3em !important;
}

.reference-modal-enter-active,
.reference-modal-leave-active {
  transition: opacity 0.3s ease;
}

.reference-modal-enter-from,
.reference-modal-leave-to {
  opacity: 0;
}

/* 체크박스 기본 스타일 완전 오버라이드 */
.no-focus-checkbox {
  appearance: none !important;
  -webkit-appearance: none !important;
  -moz-appearance: none !important;
  width: 16px !important;
  height: 16px !important;
  border: 1px solid #ccc !important;
  border-radius: 3px !important;
  background: white !important;
  outline: none !important;
  box-shadow: none !important;
  cursor: pointer !important;
  position: relative !important;
  display: inline-block !important;
}

.no-focus-checkbox:checked {
  background: white !important;
  border-color: black !important;
}

.no-focus-checkbox:checked::after {
  content: '✓' !important;
  color: black !important;
  font-size: 16px !important;
  position: absolute !important;
  top: 0px !important;
  left: 2px !important;
  line-height: 1 !important;
  background: white !important;
  height: 10px !important;
}

.no-focus-checkbox:focus,
.no-focus-checkbox:focus-visible,
.no-focus-checkbox:active,
.no-focus-checkbox:hover:focus,
.no-focus-checkbox:hover {
  outline: none !important;
  box-shadow: none !important;
  border-color: #ccc !important;
  background: white !important;
}

.no-focus-checkbox:checked:focus,
.no-focus-checkbox:checked:focus-visible,
.no-focus-checkbox:checked:active,
.no-focus-checkbox:checked:hover:focus,
.no-focus-checkbox:checked:hover {
  background: white !important;
  border-color: black !important;
  outline: none !important;
  box-shadow: none !important;
}
</style> 